import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';
import { clearChunkReloadFlag } from 'utils/lazyImport';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import 'dayjs/locale/pt';

import router from 'routes';
import ThemeCustomization from 'themes';
import ScrollTop from 'components/ScrollTop';
import CreditErrorHandler from 'components/common/CreditErrorHandler';
import ErrorBoundary from 'components/shared/ErrorBoundary';
import { AuthProvider } from 'contexts/AuthContext';
import { ShellConfigProvider } from 'contexts/ShellConfigContext';
import buildMenu from 'menu-items';
import CrmHeaderExtras from 'layout/CrmHeaderExtras';
import BranchBanner from 'components/tenants/BranchBanner';

const DATE_LOCALES = { es: 'es', en: 'en', pt: 'pt' };

function dateAdapterLocale(language) {
  const base = String(language || 'es')
    .split('-')[0]
    .toLowerCase();
  return DATE_LOCALES[base] ?? 'es';
}

// ==============================|| APP - THEME, ROUTER, LOCAL ||============================== //

export default function App() {
  const { i18n } = useTranslation();
  const adapterLocale = dateAdapterLocale(i18n.resolvedLanguage || i18n.language);

  useEffect(() => {
    clearChunkReloadFlag();
  }, []);

  useEffect(() => {
    dayjs.locale(adapterLocale);
  }, [adapterLocale]);

  const shell = useMemo(
    () => ({
      homePath: '/dashboard/default',
      buildMenu,
      HeaderExtras: CrmHeaderExtras,
      Banner: BranchBanner
    }),
    []
  );

  return (
    <LocalizationProvider key={adapterLocale} dateAdapter={AdapterDayjs} adapterLocale={adapterLocale}>
      <ThemeCustomization>
        <AuthProvider>
          <ShellConfigProvider value={shell}>
            <CreditErrorHandler />
            <ErrorBoundary>
              <ScrollTop>
                <RouterProvider router={router} />
              </ScrollTop>
            </ErrorBoundary>
          </ShellConfigProvider>
        </AuthProvider>
      </ThemeCustomization>
    </LocalizationProvider>
  );
}
